import '../ghs-heading/ghs-heading';
import { html, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import { GhsWrapper } from '../ghs-wrapper/ghs-wrapper';
import ghsWrapperStyles from '../ghs-wrapper/ghs-wrapper.css.lit';
import componentStyles from './ghs-text-card.css.lit';
import { BgVariantType } from '../ghs-wrapper/ghs-wrapper';

import '../ghs-heading/ghs-heading';
import { LightController } from '../../controllers/light-controller';

export type TextCardAlignment = 'left' | 'center' | 'right';

/**
 * The Card component.
 * @element ghs-text-card
 * @slot content - The primary slot to render the card content.
 * @slot eyebrow - The optional eyebrow text.
 * @slot links - The optional links section.
 */
@customElement('ghs-text-card')
export class GhsTextCard extends GhsWrapper {
  static styles = [ghsWrapperStyles, componentStyles];

  lightController = new LightController(
    this,
    componentStyles,
    this.tagName.toLowerCase()
  );

  /**
   * Color variant available to use as a background color for the wrapper.
   *
   * This property is passed along to the ghs-wrapper component.
   */
  @property({ type: String, attribute: 'bg-color-variant', reflect: true })
  bgColorVariant: BgVariantType;

  /**
   * Whether or not this is a nested container. If it's nested, remove padding.
   */
  @property({ type: Boolean, attribute: 'is-rounded' }) isRounded = false;

  /**
   * Whether or not this card has a shadow.
   */
  @property({ type: Boolean }) hasShadow = false;

  /**
   * Whether or not this card is being used as a basic block.
   */
  @property({ type: Boolean, attribute: 'is-basic-block', reflect: true })
  isBasicBlock = false;

  /**
   * Whether or not this card is full bleed.
   */
  @property({ type: Boolean, attribute: 'full-bleed' }) fullBleed = false;

  /**
   * Elements alignment style variant to use.
   * @param alignment
   * @default 'left'
   */
  @property({ type: String, reflect: true })
  alignment: TextCardAlignment = 'left';

  render(): TemplateResult {
    const baseClasses = this.getClasses();
    const cardClasses = {
      ...baseClasses,

      'text-card': true,
      'text-card--full-bleed': this.fullBleed,
      'outline-shadow': this.hasShadow,
      'outline-rounded-2xl': this.isRounded,
      [`text-card--${this.alignment}`]: true,
    };

    return html`
      <section class="${classMap(cardClasses)}">
        <div class="container">
          <slot name="eyebrow"></slot>
          <slot name="header"></slot>
          <slot name="content"></slot>
          <slot name="links"></slot>
        </div>
      </section>
    `;
  }
}
